import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import SharedButton from '../../components/Shared/SharedButton';
import SearchField from '../../components/Shared/SearchField';
import CustomTable from '../../components/Shared/CustomTable';
import CustomPagination from '../../components/Shared/CustomPagination';
import TableLoading from '../../components/Shared/TableLoading';
import { EditIcon, EyeIcon, CancelIcon, PrintIcon } from '../../components/Shared/TableActionIcons';
import { getERPClient } from '../../services/supplyChainApi';
import { openPrintWindow, buildSaleInvoicePrintHtml, erpBusinessToSellerSnapshot } from '../../helpers/invoicePrint';
import toast from 'react-hot-toast';

const TAB_STATUS = { all: '', cancelled: 'CANCELLED', refunded: 'REFUNDED' };

export default function ERPSaleInvoicesMain() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const business = auth?.user?.Business;

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);

  const [cancelVisible, setCancelVisible] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const [refundVisible, setRefundVisible] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundItems, setRefundItems] = useState([]);
  const [refunding, setRefunding] = useState(false);
  const [printingId, setPrintingId] = useState(null);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const erpClient = getERPClient();
      const params = {
        search: search.trim() || undefined,
        limit: 100,
        offset: 0,
      };
      const status = TAB_STATUS[tab];
      if (status) params.status = status;

      const res = await erpClient.getSaleInvoices(params);
      const list = Array.isArray(res) ? res : (res?.data || []);
      setInvoices(list);
    } catch (error) {
      console.error('Failed to fetch sale invoices:', error);
      toast.error(t('Failed to load sale invoices'));
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [search, tab, t]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handlePrint = async (row) => {
    setPrintingId(row.id);
    try {
      const erpClient = getERPClient();
      const res = await erpClient.getSaleInvoice(row.id);
      const inv = res.data || res;
      if (!inv || !inv.invoice_number) throw new Error('no invoice');
      const seller = erpBusinessToSellerSnapshot(business);
      const printInv = { ...inv, seller_snapshot: { ...(inv.seller_snapshot || {}), ...seller } };
      const html = await buildSaleInvoicePrintHtml(printInv, 'en');
      openPrintWindow(html, `Sale Invoice ${inv.invoice_number}`);
    } catch (err) {
      console.error('Failed to print sale invoice:', err);
      toast.error(t('Failed to print invoice'));
    } finally {
      setPrintingId(null);
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error(t('Please specify a cancellation reason'));
      return;
    }
    setCancelling(true);
    try {
      const erpClient = getERPClient();
      const invId = selectedInvoice?.id;
      await erpClient.cancelSaleInvoice(invId, cancelReason);
      toast.success(t('Sale Invoice cancelled'));
      setCancelVisible(false);
      setCancelReason('');
      fetchInvoices();
    } catch (err) {
      console.error('Failed to cancel invoice:', err);
      const msg = err.response?.data?.message || t('Failed to cancel invoice');
      toast.error(msg);
    } finally {
      setCancelling(false);
    }
  };

  const computeRefundAmount = (items, selected) => {
    const calcTotal = (item, refundQty) => {
      if (item.item_status === 'REFUNDED') return 0;
      const net = (Number(item.taxable_amount) || 0) + (Number(item.vat_amount) || 0);
      return net * ((Number(refundQty) || 0) / (Number(item.quantity) || 1));
    };
    const target = selected.length
      ? selected
          .map((ri) => ({ item: items.find((i) => i.item_id === ri.item_id), quantity: ri.quantity }))
          .filter((entry) => entry.item)
      : items;
    return target.reduce((s, entry) => {
      const item = entry.item || entry;
      return s + calcTotal(item, entry.quantity ?? item.quantity);
    }, 0);
  };

  const computedRefundAmount = selectedInvoice
    ? computeRefundAmount(selectedInvoice.items || [], refundItems)
    : 0;

  const toggleRefundItem = (itemId, quantity) => {
    setRefundItems((prev) => {
      const exists = prev.find((i) => i.item_id === itemId);
      if (exists) return prev.filter((i) => i.item_id !== itemId);
      return [...prev, { item_id: itemId, quantity }];
    });
  };

  const updateRefundQuantity = (itemId, quantity) =>
    setRefundItems((prev) =>
      prev.map((item) => (item.item_id === itemId ? { ...item, quantity } : item))
    );

  const handleConfirmRefund = async () => {
    if (!refundReason.trim() || computedRefundAmount <= 0) {
      toast.error(t('Please provide a refund reason and amount'));
      return;
    }
    setRefunding(true);
    try {
      const erpClient = getERPClient();
      const invId = selectedInvoice?.id;
      const items = selectedInvoice?.items || [];
      const itemIds = refundItems.length
        ? refundItems
        : items
            .filter((it) => it.item_status !== 'REFUNDED')
            .map((i) => ({ item_id: i.item_id, quantity: i.quantity }));
      const payload = {
        reason: refundReason,
        amount: Number(computedRefundAmount.toFixed(2)),
        items: itemIds.filter((it) => it.item_id),
      };
      await erpClient.refundSaleInvoice(invId, payload);
      toast.success(t('Sale Invoice refunded'));
      setRefundVisible(false);
      setRefundReason('');
      setRefundItems([]);
      fetchInvoices();
    } catch (err) {
      console.error('Failed to refund invoice:', err);
      const msg = err.response?.data?.message || t('Failed to refund invoice');
      toast.error(msg);
    } finally {
      setRefunding(false);
    }
  };

  const statusBodyTemplate = (rowData) => {
    const status = rowData.status || 'DRAFT';
    const severity =
      status === 'CANCELLED' || status === 'REFUNDED'
        ? 'danger'
        : status === 'CONFIRMED'
          ? 'success'
          : 'info';
    return <Tag value={status} severity={severity} />;
  };

  const actionBodyTemplate = (rowData) => {
    const isClosed = rowData.status === 'CANCELLED' || rowData.status === 'REFUNDED';
    return (
      <div className="flex items-center gap-2">
        <EyeIcon
          onClick={() => navigate(`/manager/erp/sale-invoices/${rowData.id}`)}
          title={t('View Details')}
        />
        {printingId === rowData.id ? (
          <i className="pi pi-spin pi-spinner text-primary" />
        ) : (
          <PrintIcon onClick={() => handlePrint(rowData)} title={t('Print')} />
        )}
        {(rowData.status === 'DRAFT' || rowData.status === 'CONFIRMED') && (
          <EditIcon
            onClick={() => navigate(`/manager/erp/sale-invoices/edit/${rowData.id}`)}
            title={t('Edit')}
          />
        )}
        {rowData.status === 'CONFIRMED' && (
          <CancelIcon
            onClick={() => {
              setSelectedInvoice(rowData);
              setCancelReason('');
              setCancelVisible(true);
            }}
            title={t('Cancel Invoice')}
          />
        )}
        {!isClosed && (
          <button
            type="button"
            onClick={() => {
              setSelectedInvoice(rowData);
              setRefundReason('');
              setRefundItems([]);
              setRefundVisible(true);
            }}
            title={t('Refund')}
            className="text-purple-600 hover:text-purple-800"
          >
            <i className="pi pi-undo" />
          </button>
        )}
      </div>
    );
  };

  const sequenceBodyTemplate = (rowData, options) => {
    return options.rowIndex + 1 + (page - 1) * perPage;
  };

  const columnsData = [
    { body: actionBodyTemplate, header: t('actions'), style: { width: '150px' } },
    { body: sequenceBodyTemplate, header: t('no'), style: { width: '3rem' } },
    { field: 'invoice_number', header: t('Invoice #'), style: { width: '150px' } },
    { body: (row) => row.buyer_name || row.buyer_snapshot?.name || '-', field: 'buyer_name', header: t('Customer') },
    { body: (row) => row.buyer_vat_number || row.buyer_snapshot?.vat_number || '-', field: 'buyer_vat_number', header: t('VAT No.'), style: { width: '120px' } },
    { body: (row) => row.created_at ? String(row.created_at).slice(0, 10) : '-', field: 'created_at', header: t('Date'), style: { width: '120px' } },
    { body: (row) => row.payment_method || '-', field: 'payment_method', header: t('Payment'), style: { width: '100px' } },
    { body: (row) => `SAR ${Number(row.grand_total || row.total_amount || 0).toFixed(2)}`, field: 'grand_total', header: t('Total'), style: { width: '120px' } },
    { body: (row) => `SAR ${Number(row.amount_paid ?? row.balance_due ?? 0).toFixed(2)}`, field: 'amount_paid', header: t('Paid'), style: { width: '120px' } },
    { body: statusBodyTemplate, field: 'status', header: t('Status'), style: { width: '120px' } },
  ];

  const paginatedData = invoices.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(invoices.length / perPage) || 1;

  const handlePageChange = (newPage, newPerPage) => {
    setPage(newPage);
    if (newPerPage) setPerPage(newPerPage);
  };

  const tabClass = (active) =>
    `px-4 py-2 rounded-md text-sm font-medium ${active
      ? 'bg-primary text-white'
      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`;

  return (
    <div>
      <div className="flex flex-col mb-2 md:flex-row justify-between items-center gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-2">
            <button
              type="button"
              className={tabClass(tab === 'all')}
              onClick={() => { setTab('all'); setPage(1); }}
            >
              {t('All')}
            </button>
            <button
              type="button"
              className={tabClass(tab === 'cancelled')}
              onClick={() => { setTab('cancelled'); setPage(1); }}
            >
              {t('Cancelled')}
            </button>
            <button
              type="button"
              className={tabClass(tab === 'refunded')}
              onClick={() => { setTab('refunded'); setPage(1); }}
            >
              {t('Refunded')}
            </button>
          </div>
          <SearchField
            className="w-full md:w-[260px]"
            placeholder={t('Search by Invoice #, Customer...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            handleClearClick={() => setSearch('')}
          />
        </div>

        <SharedButton
          label={t('Create Sale Invoice')}
          icon="pi pi-plus"
          onClick={() => navigate('/manager/erp/sale-invoices/create')}
          buttonType="primary"
        />
      </div>

      <CustomTable
        data={paginatedData}
        isLoading={loading}
        loadingIcon={<TableLoading />}
        columns={columnsData}
        rows={perPage}
        rowClassName={(row) =>
          row?.status === 'CANCELLED' || row?.status === 'REFUNDED'
            ? 'bg-red-50'
            : ''
        }
      />

      {invoices.length > 0 && (
        <CustomPagination
          currentPage={page}
          rowsPerPage={perPage}
          totalRecords={totalPages}
          totalRecord={invoices.length}
          onPageChange={handlePageChange}
        />
      )}

      <Dialog
        header={t('Sale Invoice Details')}
        visible={detailVisible}
        onHide={() => setDetailVisible(false)}
        pt={{
          root: { className: 'w-full lg:w-9/12 mx-4' },
          header: { className: '!p-2 !bg-darkBg !text-white' },
          content: { className: '!p-4' },
          closeButton: { className: 'hover:!bg-black/20 !outline-none !shadow-none' },
          closeButtonIcon: { className: '!text-white' },
        }}
      >
        {selectedInvoice && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-gray-50 p-3 rounded border">
              <div>
                <span className="text-xs text-gray-500 block">{t('Invoice #')}</span>
                <span className="font-semibold">{selectedInvoice.invoice_number}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{t('Customer')}</span>
                <span className="font-semibold">{selectedInvoice.buyer_name || selectedInvoice.buyer_snapshot?.name || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{t('Date')}</span>
                <span>{selectedInvoice.created_at ? String(selectedInvoice.created_at).slice(0, 10) : '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{t('Status')}</span>
                {statusBodyTemplate(selectedInvoice)}
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">{t('Invoice Line Items')}</h4>
              <table className="w-full text-left text-xs border">
                <thead>
                  <tr className="bg-gray-100 border-b">
                    <th className="p-2">{t('Item Code')}</th>
                    <th className="p-2">{t('Item')}</th>
                    <th className="p-2">{t('Qty')}</th>
                    <th className="p-2">{t('UOM')}</th>
                    <th className="p-2">{t('Unit Price')}</th>
                    <th className="p-2">{t('Total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedInvoice.items || []).map((item, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="p-2 font-medium">{item.item_code || '-'}</td>
                      <td className="p-2">{item.item_name || '-'}</td>
                      <td className="p-2">{item.quantity ?? '-'}</td>
                      <td className="p-2">{item.uom_name || item.uom || '-'}</td>
                      <td className="p-2">SAR {Number(item.unit_price || 0).toFixed(2)}</td>
                      <td className="p-2 font-semibold">SAR {Number(item.total_price || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end border-t pt-3">
              <div className="text-right space-y-1">
                <div>{t('Gross:')} <span className="font-medium">SAR {Number(selectedInvoice.gross_total || 0).toFixed(2)}</span></div>
                <div>{t('Discount:')} <span className="font-medium">SAR {Number(selectedInvoice.discount || 0).toFixed(2)}</span></div>
                <div>{t('VAT:')} <span className="font-medium">SAR {Number(selectedInvoice.vat_amount || 0).toFixed(2)}</span></div>
                <div className="text-base font-bold text-primary">{t('Final Total:')} SAR {Number(selectedInvoice.grand_total || 0).toFixed(2)}</div>
              </div>
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        header={t('Cancel Sale Invoice')}
        visible={cancelVisible}
        onHide={() => setCancelVisible(false)}
        pt={{
          root: { className: 'w-full max-w-md mx-4' },
          header: { className: '!p-2 !bg-darkBg !text-white' },
          content: { className: '!p-4' },
          closeButton: { className: 'hover:!bg-black/20 !outline-none !shadow-none' },
          closeButtonIcon: { className: '!text-white' },
        }}
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            {t('Are you sure you want to cancel this sale invoice? Please state the reason.')}
          </p>
          <textarea
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder={t('Cancellation reason')}
            className="w-full border border-gray-300 rounded p-2 text-sm focus:outline-none focus:border-red-500"
          />
          <div className="flex justify-end gap-2 pt-2">
            <SharedButton type="button" label={t('Dismiss')} buttonType="reset" onClick={() => setCancelVisible(false)} />
            <SharedButton type="button" label={t('Confirm Cancellation')} buttonType="danger" loading={cancelling} onClick={handleConfirmCancel} />
          </div>
        </div>
      </Dialog>

      <Dialog
        header={t('Refund Sale Invoice')}
        visible={refundVisible}
        onHide={() => setRefundVisible(false)}
        pt={{
          root: { className: 'w-full lg:w-[560px] mx-4' },
          header: { className: '!p-2 !bg-darkBg !text-white' },
          content: { className: '!p-4' },
          closeButton: { className: 'hover:!bg-black/20 !outline-none !shadow-none' },
          closeButtonIcon: { className: '!text-white' },
        }}
      >
        {selectedInvoice && (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-1">{t('Refund Amount')}</label>
              <div className="w-full border rounded-md px-3 py-2 text-sm bg-gray-50 font-semibold">
                {Number(computedRefundAmount.toFixed(2))} SAR
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-1">{t('Reason')}</label>
              <textarea
                rows={2}
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder={t('Enter refund reason...')}
                className="w-full border border-gray-300 rounded p-2 text-xs focus:outline-none focus:border-purple-500"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-1">
                {t('Items to Return')} ({t('optional, select none to return all')})
              </label>
              <div className="border rounded-md max-h-[200px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="px-2 py-1 text-left text-[11px] text-gray-500 w-8" />
                      <th className="px-2 py-1 text-left text-[11px] text-gray-500">{t('Item')}</th>
                      <th className="px-2 py-1 text-right text-[11px] text-gray-500">{t('Qty')}</th>
                      <th className="px-2 py-1 text-right text-[11px] text-gray-500">{t('Price')}</th>
                      <th className="px-2 py-1 text-right text-[11px] text-gray-500">{t('VAT')}</th>
                      <th className="px-2 py-1 text-right text-[11px] text-gray-500">{t('Total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedInvoice.items || []).map((item) => {
                      const isRefunded = item.item_status === 'REFUNDED';
                      const net = (Number(item.taxable_amount) || 0) + (Number(item.vat_amount) || 0);
                      const checked = isRefunded || !!refundItems.find((i) => i.item_id === item.item_id);
                      const qty = refundItems.find((i) => i.item_id === item.item_id)?.quantity ?? item.unrefunded_quantity ?? item.quantity;
                      return (
                        <tr key={item.item_id || item.line_number} className={`border-b last:border-b-0 ${isRefunded ? 'opacity-50' : ''}`}>
                          <td className="px-2 py-1.5">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={isRefunded}
                              onChange={() => toggleRefundItem(item.item_id, item.unrefunded_quantity ?? item.quantity)}
                              className="h-3 w-3"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            {item.item_name || item.item_code}
                            {isRefunded && <span className="text-red-500 text-[10px] ml-1">({t('REFUNDED')})</span>}
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <input
                                type="number"
                                min={0}
                                max={item.unrefunded_quantity ?? item.quantity}
                                disabled={isRefunded || !refundItems.find((i) => i.item_id === item.item_id)}
                                value={qty}
                                onChange={(e) =>
                                  updateRefundQuantity(item.item_id, Math.min(Number(e.target.value) || 0, Number(item.unrefunded_quantity ?? item.quantity)))
                                }
                                className="h-7 w-16 text-right border border-gray-300 rounded p-1 text-xs focus:outline-none"
                              />
                              <span>{item.uom || item.uom_code || item.uom_name || ''}</span>
                            </div>
                          </td>
                          <td className="px-2 py-1.5 text-right">{Number(item.unit_price || 0).toFixed(2)}</td>
                          <td className="px-2 py-1.5 text-right">{Number(item.vat_amount || 0).toFixed(2)}</td>
                          <td className="px-2 py-1.5 text-right font-semibold">{net.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <SharedButton
                type="button"
                label={t('Back')}
                buttonType="reset"
                onClick={() => {
                  setRefundVisible(false);
                  setRefundReason('');
                  setRefundItems([]);
                }}
              />
              <SharedButton
                type="button"
                label={t('Process Refund')}
                buttonType="danger"
                loading={refunding}
                disabled={!refundReason.trim() || computedRefundAmount <= 0}
                onClick={handleConfirmRefund}
              />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}